// src/TrajectoryArc.js

import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// This is the shader that will make the tube look like a glowing, moving line.
const TrajectoryMaterial = new THREE.ShaderMaterial({
  uniforms: {
    uTime: { value: 0 },
    uColor: { value: new THREE.Color('#F7921E') },
    uFocus: { value: 0.0 }, 
  },
  vertexShader: `
    attribute float progress;
    varying float vProgress;
    void main() {
      vProgress = progress;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // --- SHADER FIX: Updated fragment shader to draw the full line with a moving pulse ---
  fragmentShader: `
    uniform float uTime;
    uniform vec3 uColor;
    uniform float uFocus;
    varying float vProgress;
    
    void main() {
      // 1. Faint Base Line: A constant glow for the entire path.
      float baseAlpha = 0.1;

      // 2. Moving Pulse: The same logic as before to create a bright leading point.
      float t = fract(vProgress - uTime);
      float pulse = smoothstep(0.0, 0.03, t) - smoothstep(0.05, 0.2, t);
      
      // 3. Combine them: The total glow is the faint base line PLUS the bright pulse.
      float totalGlow = baseAlpha + pulse * 1.0;

      // 4. Modulate final alpha by the overall focus of the planet.
      // We no longer discard fragments, so the base line is always visible when in focus.
      float finalAlpha = totalGlow * pow(uFocus, 2.0);
      
      gl_FragColor = vec4(uColor, finalAlpha);
    }
  `,
  transparent: true,
  depthWrite: false, 
  blending: THREE.AdditiveBlending,
});

const TrajectoryArc = ({ start, end, speed = 1, color }) => {
  const materialRef = useRef();

  const { geometry } = useMemo(() => {
    const startVec = new THREE.Vector3(...start);
    const endVec = new THREE.Vector3(...end);

    const midPoint = new THREE.Vector3().addVectors(startVec, endVec).multiplyScalar(0.5);
    const midLength = midPoint.length();
    // --- HEIGHT FIX: Increase the multiplier to make the arc bow out more ---
    midPoint.normalize().multiplyScalar(midLength * 1.6); // Was 1.3

    const curve = new THREE.QuadraticBezierCurve3(startVec, midPoint, endVec);

    // --- WIDTH FIX: Decrease the tube radius to make the line thinner ---
    const geometry = new THREE.TubeGeometry(curve, 64, 0.002, 8, false); // Radius was 0.005

    const count = geometry.attributes.position.count;
    const progress = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      progress[i] = geometry.attributes.uv.array[i * 2];
    }
    geometry.setAttribute('progress', new THREE.BufferAttribute(progress, 1));
    
    return { geometry };
  }, [start, end]);

  useFrame((_, delta) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value += delta * speed;
    }
  });

  const material = useMemo(() => {
    const mat = TrajectoryMaterial.clone();
    mat.uniforms.uColor.value = new THREE.Color(color);
    return mat;
  }, [color]);
  
  return (
    <mesh geometry={geometry}>
      <primitive ref={materialRef} object={material} />
    </mesh>
  );
};

export default TrajectoryArc;